import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { 
  ArrowLeft, 
  Check, 
  ShieldCheck, 
  Truck, 
  CreditCard, 
  QrCode, 
  MapPin, 
  User, 
  Tag, 
  AlertCircle,
  Loader2,
  Lock
} from 'lucide-react';
import { useLojaCart } from '../../context/LojaCartContext';
import LojaService from '../../services/Loja/LojaService';
import NavBar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';

export default function LojaCheckoutView() {
  const navigate = useNavigate();
  const { 
    cartItems, 
    subtotal, 
    discount, 
    total, 
    appliedCoupon, 
    clearCart,
    selectedShipping,
    setSelectedShipping
  } = useLojaCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Etapa 1: Dados do Cliente
  const [customer, setCustomer] = useState({
    name: sessionStorage.getItem('brv-user') || '',
    cpf: '',
    email: '',
    phone: ''
  });

  // Etapa 2: Endereço
  const [address, setAddress] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  const [loadingCep, setLoadingCep] = useState(false);

  // Etapa 3: Frete
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loadingShipping, setLoadingShipping] = useState(false);

  // Etapa 4: Pagamento
  const [paymentMethod, setPaymentMethod] = useState('PIX'); // 'PIX' | 'CREDIT_CARD'
  const [card, setCard] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
    installments: 1
  });

  // Redireciona se carrinho estiver vazio
  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      navigate('/loja');
    }
  }, [cartItems, navigate]);

  // Pré-carrega dados salvos do usuário se existirem
  useEffect(() => {
    const savedUser = sessionStorage.getItem('brv-user');
    if (savedUser) {
      setCustomer(prev => (prev.name ? prev : { ...prev, name: savedUser }));
    }
  }, []);

  // Máscaras de entrada
  const formatCpf = (val) => {
    let v = val.replace(/\D/g, '').slice(0, 11);
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return v;
  };

  const formatPhone = (val) => {
    let v = val.replace(/\D/g, '').slice(0, 11);
    v = v.replace(/^(\d{2})(\d)/g, '($1) $2');
    v = v.replace(/(\d)(\d{4})$/, '$1-$2');
    return v;
  };

  const formatCep = (val) => {
    let v = val.replace(/\D/g, '').slice(0, 8);
    v = v.replace(/^(\d{5})(\d)/, '$1-$2');
    return v;
  };

  const formatCardNumber = (val) => {
    let v = val.replace(/\D/g, '').slice(0, 16);
    return v.replace(/(\d{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val) => {
    let v = val.replace(/\D/g, '').slice(0, 4);
    if (v.length > 2) v = `${v.slice(0, 2)}/${v.slice(2)}`;
    return v;
  };

  // Detecção de bandeira do cartão
  const getCardBrand = (num) => {
    const clean = num.replace(/\D/g, '');
    if (clean.startsWith('4')) return 'Visa';
    if (/^(5[1-5]|2[2-7])/.test(clean)) return 'Mastercard';
    if (/^(4011|4389|4514|4576|5041|5066|5067|5090|6277|6362|6363)/.test(clean)) return 'Elo';
    if (/^3[47]/.test(clean)) return 'Amex';
    if (/^(606282|3841)/.test(clean)) return 'Hipercard';
    return null;
  };

  // Busca CEP via ViaCEP
  const handleCepChange = async (e) => {
    const rawVal = e.target.value;
    const formatted = formatCep(rawVal);
    setAddress(prev => ({ ...prev, cep: formatted }));

    const clean = formatted.replace(/\D/g, '');
    if (clean.length === 8) {
      setLoadingCep(true);
      setErrorMsg('');
      try {
        const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setAddress(prev => ({
            ...prev,
            street: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || ''
          }));
          // Já calcula as opções de frete
          loadShippingQuotes(clean);
        } else {
          setErrorMsg('CEP não encontrado. Preencha os campos manualmente.');
        }
      } catch (err) {
        setErrorMsg('Erro ao consultar CEP. Preencha manualmente.');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  // Cotação de frete
  const loadShippingQuotes = async (cep) => {
    setLoadingShipping(true);
    try {
      const quotes = await LojaService.quoteShipping(cep, subtotal);
      setShippingOptions(quotes);
      if (quotes.length > 0) {
        setSelectedShipping(quotes[0]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingShipping(false);
    }
  };

  // Validação das Etapas
  const validateStep = (step) => {
    setErrorMsg('');
    if (step === 1) {
      if (!customer.name.trim()) { setErrorMsg('Nome completo é obrigatório.'); return false; }
      if (!customer.cpf || customer.cpf.replace(/\D/g, '').length !== 11) { setErrorMsg('CPF válido de 11 dígitos é obrigatório.'); return false; }
      if (!customer.email || !customer.email.includes('@')) { setErrorMsg('E-mail válido é obrigatório.'); return false; }
      if (!customer.phone || customer.phone.replace(/\D/g, '').length < 10) { setErrorMsg('WhatsApp com DDD é obrigatório.'); return false; }
      return true;
    }
    if (step === 2) {
      if (!address.cep || address.cep.replace(/\D/g, '').length !== 8) { setErrorMsg('CEP válido de 8 dígitos é obrigatório.'); return false; }
      if (!address.street.trim()) { setErrorMsg('Logradouro/Rua é obrigatório.'); return false; }
      if (!address.number.trim()) { setErrorMsg('Número do endereço é obrigatório.'); return false; }
      if (!address.city.trim() || !address.state.trim()) { setErrorMsg('Cidade e Estado são obrigatórios.'); return false; }
      return true;
    }
    if (step === 3) {
      if (!selectedShipping) { setErrorMsg('Selecione uma opção de entrega para prosseguir.'); return false; }
      return true;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 2 && shippingOptions.length === 0) {
        loadShippingQuotes(address.cep.replace(/\D/g, ''));
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  // Finalização do Pedido
  const handleFinalizeOrder = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (paymentMethod === 'CREDIT_CARD') {
      const cleanNum = card.number.replace(/\D/g, '');
      if (cleanNum.length < 15) { setErrorMsg('Número do cartão inválido.'); return; }
      if (!card.name.trim()) { setErrorMsg('Nome no cartão é obrigatório.'); return; }
      if (!card.expiry || card.expiry.length !== 5) { setErrorMsg('Validade do cartão (MM/AA) inválida.'); return; }
      if (!card.cvv || card.cvv.length < 3) { setErrorMsg('Código de segurança (CVV) inválido.'); return; }
    }

    setIsSubmitting(true);

    try {
      const orderPayload = {
        customer,
        shipping: {
          ...address,
          methodCode: selectedShipping.code,
          methodName: selectedShipping.name,
          cost: selectedShipping.cost
        },
        items: cartItems,
        subtotal,
        discount,
        shippingCost: selectedShipping?.cost || 0,
        totalAmount: total,
        paymentMethod,
        cardLastDigits: paymentMethod === 'CREDIT_CARD' ? card.number.replace(/\D/g, '').slice(-4) : null,
        installments: paymentMethod === 'CREDIT_CARD' ? Number(card.installments) : 1
      };

      const resultOrder = await LojaService.checkout(orderPayload);

      clearCart();
      navigate(`/loja/pedido/${resultOrder.orderNumber}`, { state: { order: resultOrder } });
    } catch (err) {
      setErrorMsg('Ocorreu um erro ao processar o seu pedido. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-urb-dark flex flex-col selection:bg-urb-primary selection:text-black">
      <NavBar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Barra Superior de Voltar & Título */}
        <div className="flex items-center justify-between pb-6 border-b border-urb-border">
          <Link
            to="/loja"
            className="inline-flex items-center gap-2 text-xs font-bold text-urb-text-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para a Loja URB</span>
          </Link>

          <div className="flex items-center gap-2 text-xs text-urb-primary font-bold">
            <Lock className="w-3.5 h-3.5" />
            <span>Checkout Criptografado SSL</span>
          </div>
        </div>

        {/* Indicador de Passos */}
        <div className="grid grid-cols-4 gap-2 sm:gap-4 my-8">
          {[
            { num: 1, label: 'Identificação', icon: User },
            { num: 2, label: 'Endereço', icon: MapPin },
            { num: 3, label: 'Entrega', icon: Truck },
            { num: 4, label: 'Pagamento', icon: CreditCard }
          ].map((st) => {
            const isCompleted = currentStep > st.num;
            const isActive = currentStep === st.num;

            return (
              <div 
                key={st.num}
                className={`flex flex-col sm:flex-row items-center gap-2 p-2.5 sm:p-3 rounded-xl border transition-all ${
                  isActive 
                    ? 'bg-urb-primary/10 border-urb-primary text-urb-primary' 
                    : isCompleted 
                    ? 'bg-urb-card border-urb-primary/30 text-white' 
                    : 'bg-urb-card/40 border-urb-border text-urb-text-muted'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${
                  isActive ? 'bg-urb-primary text-black' : isCompleted ? 'bg-urb-primary/20 text-urb-primary' : 'bg-white/10 text-urb-text-muted'
                }`}>
                  {isCompleted ? <Check className="w-4 h-4" /> : st.num}
                </div>
                <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-center sm:text-left truncate">
                  {st.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Mensagem de Erro Geral */}
        {errorMsg && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-2.5 text-xs text-red-400 font-bold">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Grid de Checkout: Formulário à Esquerda + Resumo à Direita */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Formulário Wizard (8 Colunas) */}
          <div className="lg:col-span-7 xl:col-span-8 bg-urb-card rounded-2xl border border-urb-border p-6 sm:p-8 space-y-6">
            
            {/* ETAPA 1: IDENTIFICAÇÃO */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-wide">
                    1. Dados do Corredor
                  </h3>
                  <p className="text-xs text-urb-text-secondary mt-1">
                    Insira seus dados para faturamento e emissão do comprovante oficial.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      placeholder="Ex: Rodrigo Lima Santos"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      CPF *
                    </label>
                    <input
                      type="text"
                      value={customer.cpf}
                      onChange={(e) => setCustomer({ ...customer, cpf: formatCpf(e.target.value) })}
                      placeholder="000.000.000-00"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      WhatsApp / Celular *
                    </label>
                    <input
                      type="text"
                      value={customer.phone}
                      onChange={(e) => setCustomer({ ...customer, phone: formatPhone(e.target.value) })}
                      placeholder="(11) 99999-0000"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      E-mail para Confirmação *
                    </label>
                    <input
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      placeholder="rodrigo@email.com"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>
                </div>

                <div className="pt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="px-8 py-3.5 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover transition-all cursor-pointer shadow-[0_0_15px_rgba(138,230,0,0.3)]"
                  >
                    CONTINUAR PARA ENDEREÇO
                  </button>
                </div>
              </div>
            )}

            {/* ETAPA 2: ENDEREÇO */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-wide">
                    2. Endereço de Entrega
                  </h3>
                  <p className="text-xs text-urb-text-secondary mt-1">
                    Digite seu CEP para autocompletar rua, bairro e cidade.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-5">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      CEP *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={address.cep}
                        onChange={handleCepChange}
                        placeholder="00000-000"
                        className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                      />
                      {loadingCep && (
                        <Loader2 className="w-4 h-4 text-urb-primary animate-spin absolute right-3.5 top-1/2 -translate-y-1/2" />
                      )}
                    </div>
                  </div>

                  <div className="sm:col-span-9">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Rua / Logradouro *
                    </label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      placeholder="Ex: Av. Paulista"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Número *
                    </label>
                    <input
                      type="text"
                      value={address.number}
                      onChange={(e) => setAddress({ ...address, number: e.target.value })}
                      placeholder="123"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Complemento (opcional)
                    </label>
                    <input
                      type="text"
                      value={address.complement}
                      onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                      placeholder="Apto 42, Bloco B"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      value={address.neighborhood}
                      onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })}
                      placeholder="Bela Vista"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-9">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      placeholder="São Paulo"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                      UF *
                    </label>
                    <input
                      type="text"
                      maxLength={2}
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })}
                      placeholder="SP"
                      className="w-full px-4 py-3 rounded-xl bg-urb-dark border border-urb-border text-sm text-white uppercase focus:outline-none focus:border-urb-primary"
                    />
                  </div>
                </div>

                <div className="pt-4 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="text-xs text-urb-text-secondary hover:text-white font-bold"
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="px-8 py-3.5 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover transition-all cursor-pointer shadow-[0_0_15px_rgba(138,230,0,0.3)]"
                  >
                    CONTINUAR PARA ENTREGA
                  </button>
                </div>
              </div>
            )}

            {/* ETAPA 3: FRETE */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-wide">
                    3. Método de Envio
                  </h3>
                  <p className="text-xs text-urb-text-secondary mt-1">
                    Escolha a melhor opção para receber seu equipamento.
                  </p>
                </div>

                {loadingShipping ? (
                  <div className="p-8 text-center space-y-2">
                    <Loader2 className="w-6 h-6 text-urb-primary animate-spin mx-auto" />
                    <p className="text-xs text-urb-text-muted">Calculando rotas e opções de envio...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shippingOptions.map((opt) => {
                      const isSelected = selectedShipping?.code === opt.code;
                      const isFreeCoupon = appliedCoupon?.type === 'free_shipping';
                      const finalCost = isFreeCoupon ? 0 : opt.cost;

                      return (
                        <div
                          key={opt.code}
                          onClick={() => setSelectedShipping({ ...opt, cost: finalCost })}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-urb-primary/10 border-urb-primary ring-1 ring-urb-primary'
                              : 'bg-urb-dark border-urb-border hover:border-urb-primary/40'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                              isSelected ? 'border-urb-primary bg-urb-primary' : 'border-urb-border'
                            }`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-black" />}
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-white">
                                {opt.name}
                              </h4>
                              <p className="text-xs text-urb-text-secondary mt-0.5">
                                {opt.description} • Prazo: {opt.days} {opt.days === 1 ? 'dia útil' : 'dias úteis'}
                              </p>
                            </div>
                          </div>

                          <div className="text-right">
                            <span className="text-sm font-black text-white">
                              {finalCost === 0 ? (
                                <span className="text-urb-primary uppercase">Grátis</span>
                              ) : (
                                `R$ ${finalCost.toFixed(2).replace('.', ',')}`
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="pt-4 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="text-xs text-urb-text-secondary hover:text-white font-bold"
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="px-8 py-3.5 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover transition-all cursor-pointer shadow-[0_0_15px_rgba(138,230,0,0.3)]"
                  >
                    CONTINUAR PARA PAGAMENTO
                  </button>
                </div>
              </div>
            )}

            {/* ETAPA 4: PAGAMENTO */}
            {currentStep === 4 && (
              <form onSubmit={handleFinalizeOrder} className="space-y-6">
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-wide">
                    4. Forma de Pagamento
                  </h3>
                  <p className="text-xs text-urb-text-secondary mt-1">
                    Escolha entre PIX dinâmico instantâneo ou Cartão de Crédito em até 6x.
                  </p>
                </div>

                {/* Seletor PIX / Cartão */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('PIX')}
                    className={`flex items-center justify-center gap-2.5 p-4 rounded-xl border text-xs sm:text-sm font-bold uppercase transition-all cursor-pointer ${
                      paymentMethod === 'PIX'
                        ? 'bg-urb-primary text-black border-urb-primary shadow-[0_0_15px_rgba(138,230,0,0.25)] font-black'
                        : 'bg-urb-dark border-urb-border text-urb-text-secondary hover:text-white'
                    }`}
                  >
                    <QrCode className="w-5 h-5" />
                    <span>PIX Instantâneo</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('CREDIT_CARD')}
                    className={`flex items-center justify-center gap-2.5 p-4 rounded-xl border text-xs sm:text-sm font-bold uppercase transition-all cursor-pointer ${
                      paymentMethod === 'CREDIT_CARD'
                        ? 'bg-urb-primary text-black border-urb-primary shadow-[0_0_15px_rgba(138,230,0,0.25)] font-black'
                        : 'bg-urb-dark border-urb-border text-urb-text-secondary hover:text-white'
                    }`}
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>Cartão de Crédito</span>
                  </button>
                </div>

                {/* Painel PIX */}
                {paymentMethod === 'PIX' && (
                  <div className="p-5 rounded-2xl bg-urb-dark border border-urb-primary/30 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center text-urb-primary shrink-0">
                        <QrCode className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-white">
                          Aprovação Imediata via PIX
                        </h4>
                        <p className="text-xs text-urb-text-secondary">
                          Após confirmar, você receberá o QR Code oficial e o código Copia e Cola EMV para pagamento no seu banco.
                        </p>
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-urb-card border border-urb-border/50 text-xs text-urb-text-secondary flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-urb-primary shrink-0" />
                      <span>Liberação automática em até 10 segundos após a transferência.</span>
                    </div>
                  </div>
                )}

                {/* Painel Cartão de Crédito */}
                {paymentMethod === 'CREDIT_CARD' && (
                  <div className="p-5 rounded-2xl bg-urb-dark border border-urb-border space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase text-urb-text-muted">
                        Dados do Cartão de Crédito
                      </span>
                      {getCardBrand(card.number) && (
                        <span className="px-2.5 py-0.5 rounded-md bg-urb-primary/10 border border-urb-primary/30 text-urb-primary font-bold text-xs uppercase">
                          {getCardBrand(card.number)}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                          Número do Cartão *
                        </label>
                        <input
                          type="text"
                          value={card.number}
                          onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                          placeholder="0000 0000 0000 0000"
                          className="w-full px-4 py-3 rounded-xl bg-urb-card border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary font-mono"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                          Nome Impresso no Cartão *
                        </label>
                        <input
                          type="text"
                          value={card.name}
                          onChange={(e) => setCard({ ...card, name: e.target.value.toUpperCase() })}
                          placeholder="RODRIGO L SANTOS"
                          className="w-full px-4 py-3 rounded-xl bg-urb-card border border-urb-border text-sm text-white uppercase focus:outline-none focus:border-urb-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                          Validade (MM/AA) *
                        </label>
                        <input
                          type="text"
                          value={card.expiry}
                          onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })}
                          placeholder="12/28"
                          className="w-full px-4 py-3 rounded-xl bg-urb-card border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                          CVV / Segurança *
                        </label>
                        <input
                          type="password"
                          maxLength={4}
                          value={card.cvv}
                          onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, '') })}
                          placeholder="123"
                          className="w-full px-4 py-3 rounded-xl bg-urb-card border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary font-mono"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-black uppercase text-urb-text-muted mb-1.5">
                          Parcelamento *
                        </label>
                        <select
                          value={card.installments}
                          onChange={(e) => setCard({ ...card, installments: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-urb-card border border-urb-border text-sm text-white focus:outline-none focus:border-urb-primary cursor-pointer"
                        >
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <option key={n} value={n} className="bg-urb-dark text-white">
                              {n}x de R$ {(total / n).toFixed(2).replace('.', ',')} sem juros
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="text-xs text-urb-text-secondary hover:text-white font-bold"
                  >
                    Voltar
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-4 rounded-xl bg-urb-primary text-black font-black text-sm uppercase tracking-wider hover:bg-urb-primary-hover transition-all cursor-pointer shadow-[0_0_20px_rgba(138,230,0,0.3)] flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>PROCESSANDO PEDIDO...</span>
                      </>
                    ) : (
                      <span>CONCLUIR PEDIDO (R$ {total.toFixed(2).replace('.', ',')})</span>
                    )}
                  </button>
                </div>
              </form>
            )}

          </div>

          {/* Resumo Permanente Lateral (4 Colunas) */}
          <div className="lg:col-span-5 xl:col-span-4 bg-urb-card rounded-2xl border border-urb-border p-6 space-y-6 sticky top-24">
            <h3 className="text-base font-extrabold text-white uppercase tracking-wider flex items-center gap-2 border-b border-urb-border pb-3">
              <span>Resumo da Compra</span>
              <span className="text-xs font-bold text-urb-primary">
                ({cartItems.reduce((acc, i) => acc + i.quantity, 0)} itens)
              </span>
            </h3>

            {/* Lista dos Itens */}
            <div className="space-y-3 max-h-64 overflow-y-auto no-scrollbar">
              {cartItems.map((item) => (
                <div key={item.itemKey} className="flex gap-3 items-center">
                  <div className="w-12 h-12 rounded-lg bg-urb-dark p-1 border border-urb-border shrink-0 flex items-center justify-center">
                    <img src={item.image} alt={item.productName} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0 text-xs">
                    <h4 className="font-bold text-white truncate">{item.productName}</h4>
                    <span className="text-urb-text-muted">
                      {item.size} • {item.color} (x{item.quantity})
                    </span>
                  </div>
                  <span className="text-xs font-black text-white shrink-0">
                    R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              ))}
            </div>

            {/* Cupom Aplicado */}
            {appliedCoupon && (
              <div className="p-3 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-between text-xs text-urb-primary font-bold">
                <div className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  <span>CUPOM: {appliedCoupon.code}</span>
                </div>
                <span>- R$ {discount.toFixed(2).replace('.', ',')}</span>
              </div>
            )}

            {/* Linhas de Cálculo */}
            <div className="space-y-2 text-xs text-urb-text-secondary border-t border-urb-border pt-4">
              <div className="flex justify-between">
                <span>Subtotal dos Produtos</span>
                <span className="text-white font-medium">R$ {subtotal.toFixed(2).replace('.', ',')}</span>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-urb-primary">
                  <span>Desconto de Cupom</span>
                  <span className="font-bold">- R$ {discount.toFixed(2).replace('.', ',')}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>Frete ({selectedShipping?.name || 'A calcular'})</span>
                <span className="text-white font-medium">
                  {!selectedShipping ? 'R$ 0,00' : selectedShipping.cost === 0 ? 'Grátis' : `R$ ${selectedShipping.cost.toFixed(2).replace('.', ',')}`}
                </span>
              </div>

              <div className="flex justify-between items-baseline pt-3 border-t border-urb-border text-base font-black text-white">
                <span className="uppercase tracking-wider">Total</span>
                <span className="text-2xl text-urb-primary">
                  R$ {total.toFixed(2).replace('.', ',')}
                </span>
              </div>
            </div>

            {/* Selos de Confiança */}
            <div className="pt-2 border-t border-urb-border/50 space-y-2 text-[11px] text-urb-text-muted">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-urb-primary shrink-0" />
                <span>Primeira troca grátis em até 30 dias</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-urb-primary shrink-0" />
                <span>Código de rastreamento enviado via WhatsApp</span>
              </div>
            </div>

          </div>

        </div>

      </main>

      <Footer />
    </div>
  );
}
